import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Trash2, ShoppingCart, Star, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { API } from '@/api';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useLang } from '../contexts/LanguageContext';
import { getLocalizedField, pickLocalizedText } from '../utils/localizedContent';


export default function Wishlist() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);
  const { user } = useAuth();
  const { addItem } = useCart();
  const { t, lang } = useLang();
  const text = (es, en) => pickLocalizedText(lang, es, en);

  useEffect(() => {
    if (user) {
      axios.get(`${API}/wishlist`).then(r => {
        setItems(r.data.items || []);
      }).catch(() => {}).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [user]);

  const handleRemove = async (productId) => {
    setRemoving(productId);
    try {
      await axios.delete(`${API}/wishlist/${productId}`);
      setItems(items.filter(i => i.product_id !== productId));
    } catch (err) {
      console.error(err);
    } finally {
      setRemoving(null);
    }
  };

  const handleAddToCart = (product) => {
    addItem(product);
  };

  if (!user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Heart size={64} className="mx-auto mb-4 text-[#C9A962]/30" />
          <h2 className="text-2xl font-bold text-foreground mb-2" style={{ fontFamily: 'Cinzel, serif' }}>
            {t('wishlist.title')}
          </h2>
          <p className="text-muted-foreground">{text('Inicia sesión para ver tu lista', 'Log in to see your wishlist')}</p>
          <Link to="/auth" className="btn-primary mt-4 inline-flex items-center gap-2">
            {t('auth.login')} <ChevronRight size={16} />
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="text-center py-20 text-muted-foreground">{t('common.loading')}</div>;
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-8 page-enter">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#C45B4A] to-[#E57373] flex items-center justify-center">
          <Heart size={24} className="text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Cinzel, serif' }}>{t('wishlist.title')}</h1>
          <p className="text-muted-foreground text-sm">{items.length} {text('productos guardados', 'saved products')}</p>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 bg-card border border-border rounded-2xl">
          <Heart size={64} className="mx-auto mb-4 text-[#C9A962]/20" />
          <h3 className="text-xl text-foreground font-semibold mb-2">{t('wishlist.empty')}</h3>
          <p className="text-muted-foreground text-sm mb-6">{text('Guarda productos que te gusten para comprarlos más tarde', 'Save products you like to buy them later')}</p>
          <Link to="/store" className="btn-primary inline-flex items-center gap-2">
            {t('nav.store')} <ChevronRight size={16} />
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map(product => (
            <div key={product.product_id} className="bg-card border border-border rounded-2xl overflow-hidden group hover:border-primary/40 transition-all" data-testid={`wishlist-item-${product.product_id}`}>
              {/* Image */}
              <Link to={`/store/${product.product_id}`} className="block relative aspect-[4/3] overflow-hidden">
                <img 
                  src={product.image_url || 'https://images.unsplash.com/photo-1611891487122-207579d67d98?w=400&auto=format&fit=crop'} 
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                {product.stock === 0 && (
                  <div className="absolute inset-0 bg-[#0A1612]/70 flex items-center justify-center">
                    <span className="bg-[#C45B4A] text-white px-3 py-1 rounded-full text-xs font-bold">
                      {t('store.out_of_stock')}
                    </span>
                  </div>
                )}
              </Link>
              
              {/* Content */}
              <div className="p-4">
                <Link to={`/store/${product.product_id}`}>
                  <h3 className="text-foreground font-semibold text-sm mb-1 hover:text-primary transition-colors line-clamp-1">
                    {getLocalizedField(product, 'name', lang, product.name)}
                  </h3>
                </Link>
                
                {product.rating && (
                  <div className="flex items-center gap-1 mb-2">
                    <Star size={12} className="text-[#C9A962] fill-[#C9A962]" />
                    <span className="text-muted-foreground text-xs">{product.rating.toFixed(1)}</span>
                  </div>
                )}
                
                <div className="flex items-center justify-between mt-3">
                  <span className="text-[#C9A962] font-bold text-lg">{product.price?.toFixed(2)}€</span>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => handleRemove(product.product_id)}
                      disabled={removing === product.product_id}
                      className="p-2 rounded-lg bg-[#C45B4A]/20 text-[#F87171] hover:bg-[#C45B4A]/30 transition-colors"
                      title={t('wishlist.remove')}
                      data-testid={`remove-wishlist-${product.product_id}`}
                    >
                      {removing === product.product_id ? (
                        <div className="w-4 h-4 border-2 border-[#F87171] border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <Trash2 size={16} />
                      )}
                    </button>
                    {product.stock > 0 && (
                      <button 
                        onClick={() => handleAddToCart(product)}
                        className="p-2 rounded-lg bg-[#C9A962]/20 text-[#C9A962] hover:bg-[#C9A962]/30 transition-colors"
                        title={t('store.add_to_cart')}
                        data-testid={`add-cart-${product.product_id}`}
                      >
                        <ShoppingCart size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
